export type Photo = {
  slug: string;
  width: number;
  height: number;
  lqip: string;
  position?: string;
  alt: string;
};

export const PHOTOS = {
  before: {
    slug: "before",
    width: 3120,
    height: 4160,
    position: "50% 40%",
    alt: "Before the makeup — bare skin, freshly cleaned and prepped",
    lqip:
      "data:image/webp;base64,UklGRsAAAABXRUJQVlA4ILQAAADQBQCdASoSABgAPu1urlIppiQiqAgBMB2JQBajYdA3H0C+lpf9mCrf12/59HfqZB5Um0O4lKoAAPw6rMpf8eah9u75601O40yq/dWgY/cBt1t4r+OR+ME39sldqpukVaJdRjx8nkfq8MOP7U7ZGPuPuY3BYYDn9OyR2qZdevXzeKLZTtuDdXUe5My11Icqc1WEmCIbl/FyBdz4T3Q7CWcr+tDhc1yLXW2vjUFCHiDoyYYmYAA=",
  },
  after: {
    // Pre-cropped so the face matches `before` exactly (see alignment notes),
    // which is what makes the before/after wipe hold steady.
    slug: "after",
    width: 1984,
    height: 2644,
    position: "50% 40%",
    alt: "After the makeup — the finished signature look",
    lqip:
      "data:image/webp;base64,UklGRuYAAABXRUJQVlA4INoAAACwBQCdASoUABsAPu1or1AppaSiqAqpMB2JbACdMuN2da3AKIqeY5nizK1fJ/TJCnkFjRmDAgAA/nG7cSMzObNDNTa3ypMlHWzdnwELFTcwC1AC7rW1lXFVVZrjliJH4QTG8tBFbo8UVoqVM5NIqCXTi29eypbV27j+qscl8SmvzukTkifwxzt9dRORSXBAkvCnfXjdSjlFjxwA7jEooxPQJdZOhoMDtcorJifrzbdceHx65aY96kMneV0teS5PLBXDVLLgO+o7STNuts/xqSbN0+STlIR6/UAAAA==",
  },
  founder: {
    // 1280x1280 source, cropped to 4:5 with the face centred horizontally
    slug: "founder",
    width: 1024,
    height: 1280,
    position: "50% 38%",
    alt: "Aditi Sah, the makeup artist behind the look",
    lqip:
      "data:image/webp;base64,UklGRtQAAABXRUJQVlA4IMgAAADQBACdASoUABkAPu1sqlGppaOiqAqpMB2JZgCdMzGgAADZi/KWDFowd6dpY1WpAAD+tZr8wBNfD5bJpHht40/rU4bz5cMzRPcgicVYdHb1Cp7xsnSCDIfJR8wK5hBsH9TI+cHnfbw8ifwPo+KqVDIIAuhZeJCZmFlUibnSxo0R9s/XZf0DN9O9OCARHkWk7x/hNg98Q18i8gnlOAdWVXqq5GnC4XjAKTNmp2kwqg33hzL/awh093Dyw9Cpb5s3KuuDbgrCYaQAAA==",
  },
  look1: {
    slug: "look-1",
    width: 4160,
    height: 6240,
    position: "50% 40%",
    alt: "The finished look, front view",
    lqip:
      "data:image/webp;base64,UklGRoQAAABXRUJQVlA4IHgAAAAwBACdASoQABgAPu1iqU2ppaOiMAgBMB2JYwCdMoACbPZb47KTk4qCyVAA/uv97EnjyATq5vu6SIeXexcrZNj1DD3gs2QYsvozlVudz8zzWVvitov9Wt7TtoSYgb8/3tHqtQIHSe8wHFVepxntzKw+OwSnL1J4AAA=",
  },
  look2: {
    slug: "look-2",
    width: 4160,
    height: 6240,
    position: "50% 40%",
    alt: "The finished look, three-quarter angle",
    lqip:
      "data:image/webp;base64,UklGRpYAAABXRUJQVlA4IIoAAABQBACdASoQABgAPu1iqU2ppaQiMAgBMB2JYwC7AYw6xo1NpNhIet3ejUBAAP7r/eKrMAkhXlzZMhAqupBbaQVH050MYQsdKpqfamC3fkowI+9kRKb+GlDGtd24z/6YWqlXnzJipQj4rH89ZL1Qsl38hCRVnQQJERksho2z/OjgggVMbNg2lpbZgAA=",
  },
  look3: {
    slug: "look-3",
    width: 4160,
    height: 6240,
    position: "50% 40%",
    alt: "The finished look, close detail",
    lqip:
      "data:image/webp;base64,UklGRpwAAABXRUJQVlA4IJAAAACQBACdASoQABgAPu1kqU2ppaQiMAgBMB2JYgCdAdwA3RS+pooweG5GZ4FDkAAA/utze3Kg9XFyz0P3mu/4v3sF/cZmU61ZC0Tk+A70+eXpNenIjKo91SzjuPGXFlCe6J0sy6+XNfMlWe4Cp47uTt5vvocAYhVMLwb99S2xn8mCY4ENM2x8QoDDqotTE8AEAAA=",
  },
} satisfies Record<string, Photo>;

const WIDTHS = [480, 900, 1400, 2000];

export function availableWidths(photo: Photo) {
  return WIDTHS.filter(w => w < photo.width);
}

export function srcset(photo: Photo) {
  return availableWidths(photo)
    .map(w => `/photos/${photo.slug}-${w}.webp ${w}w`)
    .join(", ");
}

export function fallbackSrc(photo: Photo) {
  const widths = availableWidths(photo);
  return `/photos/${photo.slug}-${widths[widths.length - 1]}.webp`;
}

/**
 * Resolves a stored image URL back to its build-time photo, if it points at
 * one of the responsive variants in `public/photos`.
 *
 * Portfolio rows store a single URL, but the curated work ships at four
 * widths. Matching the URL back to its manifest entry lets `/art` serve the
 * same responsive set as the rest of the site, with no extra column and no
 * duplication of the width table.
 */
const BY_SLUG = new Map(
  Object.values(PHOTOS).map(photo => [photo.slug, photo] as const),
);

export function photoForUrl(url: string): Photo | null {
  const match = /^\/photos\/([a-z0-9-]+)-\d+\.webp$/i.exec(url);
  if (!match) return null;
  return BY_SLUG.get(match[1] ?? "") ?? null;
}

export const LIGHTBOX_ORDER = [
  PHOTOS.before,
  PHOTOS.after,
  PHOTOS.look1,
  PHOTOS.look2,
  PHOTOS.look3,
];
